import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Warning, SignOut, Circle, Bell, Check } from "@phosphor-icons/react";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [emCount, setEmCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const load = () => {
      api.get("/emergencies?status=ACTIVE").then((r) => setEmCount(r.data.emergencies.length)).catch(() => {});
      api.get("/notifications").then((r) => { setNotifs(r.data.notifications); setUnread(r.data.unread_count); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); setUnread(0); api.get("/notifications").then((r) => setNotifs(r.data.notifications)); };
  const doLogout = async () => { await logout(); nav("/login"); };
  const hasEmergency = emCount > 0;

  return (
    <header data-testid="app-header"
      className={`h-14 shrink-0 flex items-center justify-between px-5 border-b border-border bg-card ${hasEmergency ? "emergency-active-header" : ""}`}>
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-xs text-[#34C759] font-semibold">
          <Circle size={8} weight="fill" className="animate-pulse" /> LIVE
        </span>
        <span className="text-xs text-muted-foreground">Telemetry streaming · 4s interval</span>
      </div>

      <div className="flex items-center gap-4">
        {hasEmergency && (
          <button data-testid="emergency-header-alert" onClick={() => nav("/emergency")}
            className="pulse-danger flex items-center gap-2 bg-destructive text-white text-xs font-bold px-3 py-1.5 rounded-sm">
            <Warning size={16} weight="fill" /> {emCount} ACTIVE EMERGENCY
          </button>
        )}

        {/* Notifications bell */}
        <div className="relative" ref={ref}>
          <button data-testid="notifications-bell" onClick={() => setOpen((o) => !o)}
            className="relative w-9 h-9 rounded-sm border border-border hover:border-[#3f3f46] flex items-center justify-center text-muted-foreground hover:text-foreground">
            <Bell size={17} weight="bold" />
            {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{unread}</span>}
          </button>
          {open && (
            <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 bg-popover border border-border rounded-sm shadow-xl z-50 max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border sticky top-0 bg-popover">
                <span className="label-caps">Notifications</span>
                <button data-testid="notif-read-all" onClick={markAll} className="text-xs text-primary flex items-center gap-1"><Check size={12} weight="bold" /> Mark all read</button>
              </div>
              {notifs.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications</div>}
              {notifs.map((n) => (
                <div key={n.id} className={`px-3 py-2 border-b border-border/50 ${!n.read ? "bg-primary/5" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold" style={{ color: n.severity === "high" ? "#FF9500" : "#5AC8FA" }}>{n.title}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(n.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{n.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-right leading-tight">
          <div className="text-sm font-semibold">{user?.name}</div>
          <div className="label-caps text-[9px]">{user?.role_label}</div>
        </div>
        <button data-testid="logout-button" onClick={doLogout}
          className="w-9 h-9 rounded-sm border border-border hover:border-[#3f3f46] flex items-center justify-center text-muted-foreground hover:text-foreground">
          <SignOut size={17} weight="bold" />
        </button>
      </div>
    </header>
  );
}
